import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import {
  Navbar, Footer, Sidebar, ThemeSettings, AutoLogoutHandler, Forgot, ResetPassword,
  BasicTabs, FlipOrig, Recordings, OTA, Jobs, AssociateSchedule, Events, HTMLFiles, OrgChart,
} from './components';
import {
  Organizations, Alerts, Risks, Analytics, OrgAnalyticsPage, Onboarding, Calendar, Campaign,
  Associates, Associate, Triage, AssociatePage, AssociateLayout, MobileAssociateLayout, Calliope,
  AssociatesControlCenter, AssociatesDashboard, InventoryControlCenter, ManagerControlCenter,
  MobileManager, Stats, Inventory, Inventory2, InventoryPage, Inv, Users, Managers, Manager,
  Reports, NotificationEvents, Roles, Templates, NotFound, Login,
} from './pages';
import { api, clearSession, readUser, writeUser } from './api/client';
import { useStateContext } from './contexts/ContextProvider';
import './App.css';

const App = () => {
  const {
    currentMode, activeMenu, themeSettings, loggedIn, setLoggedIn, isMobile,
  } = useStateContext();
  const [, setEmail] = useState('');
  const [tabNo] = useState(0);

  useEffect(() => {
    const session = readUser();
    if (!session?.token) {
      setLoggedIn(false);
      return undefined;
    }
    let cancelled = false;
    api('/verify', {
      method: 'POST',
      anonymous: true,
      body: {
        email: session.email,
        token: session.token,
        _id: session._id,
        organizationid: session.organizationid,
      },
    })
      .then((result) => {
        if (cancelled) return;
        const ok = result?.message === 'success';
        setLoggedIn(ok);
        writeUser({
          _id: result._id,
          loggedIn: ok,
          message: result.message,
          email: result.email,
          token: result.token,
          organizationid: result.organizationid,
        });
        setEmail(session.email || '');
      })
      .catch(() => {
        if (cancelled) return;
        clearSession();
        setLoggedIn(false);
        window.location.reload();
      });
    return () => {
      cancelled = true;
    };
  }, [setLoggedIn]);

  const gate = (element) => (loggedIn ? element : <Navigate to="/login" replace />);
  const session = readUser();
  let home = '/associatescontrolcenter';
  if (isMobile) {
    home = '/associatelayout';
  } else if (session?.landingPage) {
    home = `/${session.landingPage}`;
  }

  return (
    <div className={currentMode === 'Dark' ? 'dark' : ''}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="flex relative dark:bg-main-dark-bg max-w-full overflow-x-hidden">
          {loggedIn && activeMenu ? (
            <div className="w-72 fixed sidebar dark:bg-secondary-dark-bg bg-white z-20">
              <Sidebar />
            </div>
          ) : (
            <div className="w-0 overflow-hidden dark:bg-secondary-dark-bg">
              {loggedIn ? <Sidebar /> : null}
            </div>
          )}
          <div className={loggedIn && activeMenu
            ? 'dark:bg-main-dark-bg bg-main-bg min-h-screen md:ml-72 w-full min-w-0 max-w-full'
            : 'bg-main-bg dark:bg-main-dark-bg w-full min-w-0 max-w-full min-h-screen'}
          >
            <div className="sticky top-0 bg-main-bg dark:bg-main-dark-bg navbar w-full z-10">
              {loggedIn && <Navbar />}
            </div>
            <div className="min-w-0 max-w-full">
              {themeSettings && <ThemeSettings />}
              <AutoLogoutHandler>
                <Routes>
                  <Route path="/" element={loggedIn ? <Navigate to={home} replace /> : <Login setLoggedIn={setLoggedIn} setEmail={setEmail} />} />
                  <Route path="/login" element={loggedIn ? <Navigate to={home} replace /> : <Login setLoggedIn={setLoggedIn} setEmail={setEmail} />} />
                  <Route path="/forgot" element={<Forgot />} />
                  <Route path="/resetPassword" element={<ResetPassword />} />
                  <Route path="/hipaa" element={<HTMLFiles props={{ mdfile: 'hipaa.md' }} />} />
                  <Route path="/privacy" element={<HTMLFiles props={{ mdfile: 'privacy.md' }} />} />
                  <Route path="/tos" element={<HTMLFiles props={{ mdfile: 'tos.md' }} />} />

                  <Route path="/organizations" element={gate(<Organizations />)} />
                  <Route path="/alerts" element={gate(<Alerts />)} />
                  <Route path="/risks" element={gate(<Risks />)} />
                  <Route path="/dashboard" element={gate(<Analytics />)} />
                  <Route path="/analytics" element={gate(<OrgAnalyticsPage />)} />
                  <Route path="/onboarding" element={gate(<Onboarding />)} />
                  <Route path="/associatesv0" element={gate(<Associates />)} />
                  <Route path="/associates" element={gate(<Associate />)} />
                  <Route path="/associatestriage" element={gate(<Triage />)} />
                  <Route path="/associatepage" element={gate(<AssociatePage />)} />
                  {!isMobile && <Route path="/associatelayout" element={gate(<AssociateLayout />)} />}
                  {isMobile && <Route path="/associatelayout" element={gate(<MobileAssociateLayout />)} />}
                  <Route path="/mobilelayout" element={gate(<MobileAssociateLayout />)} />
                  <Route path="/associatescontrolcenter" element={gate(<AssociatesControlCenter />)} />
                  <Route path="/associatesdashboard" element={gate(<AssociatesDashboard />)} />
                  <Route path="/inventorycontrolcenter" element={gate(<InventoryControlCenter />)} />
                  {!isMobile && <Route path="/managercontrolcenter" element={gate(<ManagerControlCenter />)} />}
                  {isMobile && <Route path="/managercontrolcenter" element={gate(<MobileManager />)} />}
                  <Route path="/users" element={gate(<Users />)} />
                  <Route path="/managers" element={gate(<Managers />)} />
                  <Route path="/manager" element={gate(<Manager />)} />
                  <Route path="/roles" element={gate(<Roles />)} />
                  <Route path="/templates" element={gate(<Templates />)} />
                  <Route path="/events" element={gate(<Events />)} />
                  <Route path="/notificationevents" element={gate(<NotificationEvents />)} />
                  <Route path="/basic" element={gate(<BasicTabs tabNo={tabNo} />)} />
                  <Route path="/cardflip" element={gate(<FlipOrig />)} />
                  <Route path="/reports" element={gate(<Reports />)} />
                  <Route path="/inventoryv0" element={gate(<Inv />)} />
                  <Route path="/inventorypage" element={gate(<InventoryPage />)} />
                  <Route path="/card" element={gate(<Inventory />)} />
                  <Route path="/inventory" element={gate(<Inventory2 />)} />
                  <Route path="/calendar" element={gate(<Calendar />)} />
                  <Route path="/campaign" element={gate(<Campaign />)} />
                  <Route path="/recordings" element={gate(<Recordings />)} />
                  <Route path="/ota" element={gate(<OTA />)} />
                  <Route path="/jobs" element={gate(<Jobs />)} />
                  <Route path="/associateschedule" element={gate(<AssociateSchedule />)} />
                  <Route path="/calliope" element={gate(<Calliope />)} />
                  <Route path="/orgchart" element={gate(<OrgChart />)} />
                  <Route path="/stats" element={gate(<Stats />)} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AutoLogoutHandler>
            </div>
            {loggedIn && <Footer />}
          </div>
        </div>
      </BrowserRouter>
    </div>
  );
};

export default App;
